// WAAP "API Protection → Discovery" — custom discovery rules + sampling config.
// Account-level (any of the account's domain ids resolves the customer).
//   GET/POST   /api-protection/discovery/rules
//   PUT/DELETE /api-protection/discovery/rules/:ruleId
//   GET/PUT    /api-protection/discovery/config
//
// NOTE — stays on the PRIVATE surface (NOT migrated to public Open-API).
// A public route GET/POST /waap/domains/:id/api-app (+ /:appId GET/PUT/DELETE)
// exists, but it is a DIFFERENT resource: the vendor's apiProtection/apiApps "API
// Applications" (id/name/websites/domain/path_prefix/service_ids — a domain+pathPrefix
// grouping that API Protection guards). Discovery below manages auto-discovery *rules*
// (match conditions on path/ua/header/method/...) plus traffic *sampling* cadence —
// no field or semantic overlap with api-app. Migrating here would be a mismatch; if an
// "API Applications" screen is ever needed, add a separate public FE feature for it.
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

export interface WaapDiscoveryCondition {
  key: string; // path | ua | request_header | http_method | response_content_type | domain
  condition: string; // contain | equal | not_contain
  value: string;
}

export interface WaapDiscoveryRule {
  id: number;
  name: string;
  type: number; // 1 = system, 2 = custom
  description: string;
  exclude_static_files: boolean;
  exclude_ajax: boolean;
  conditions: WaapDiscoveryCondition[];
}

export interface WaapDiscoveryRules {
  rules: WaapDiscoveryRule[];
}

export interface WaapDiscoverySampling {
  enabled: boolean;
  /** Minutes as a string: "60" = Every hour, "1440" = Every day. */
  frequency: string;
}

const rulesBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionDiscoveryRules);
const configPath = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionDiscoveryConfig);

const rulesKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionDiscoveryRules);
const configKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionDiscoveryConfig);

/** List all discovery rules (system + custom). The UI shows only custom (type 2). */
export function useWaapDiscoveryRules(domainId: string | undefined) {
  return useQuery({
    queryKey: rulesKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapDiscoveryRule[]> => {
      const res = await serviceApiV3.get<WaapDiscoveryRules>(rulesBase(domainId as string));
      return res.data?.rules ?? [];
    },
  });
}

/** Create a custom discovery rule. */
export function useCreateWaapDiscoveryRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapDiscoveryRule>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapDiscoveryRule) => (await serviceApiV3.post(rulesBase(domainId), body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Edit a custom discovery rule. */
export function useUpdateWaapDiscoveryRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapDiscoveryRule }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapDiscoveryRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a custom discovery rule. */
export function useDeleteWaapDiscoveryRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await serviceApiV3.delete(`${rulesBase(domainId)}/${id}`)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Read the API request/response sampling settings. */
export function useWaapDiscoverySampling(domainId: string | undefined) {
  return useQuery({
    queryKey: configKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapDiscoverySampling> => {
      const res = await serviceApiV3.get<WaapDiscoverySampling>(configPath(domainId as string));
      return res.data ?? { enabled: false, frequency: '60' };
    },
  });
}

/** Save the API request/response sampling settings. */
export function useSaveWaapDiscoverySampling(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapDiscoverySampling>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapDiscoverySampling) => (await serviceApiV3.put(configPath(domainId), body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: configKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}
